import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { Box, Button, SxProps, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useCallback, useMemo } from 'react';

import {
  BROWSE_MARKETPLACE_LABEL,
  ENTRY_BANNER_BODY,
  ENTRY_BANNER_TITLE,
  MARKETPLACE_ENTRY_ICON_SIZE,
  MARKETPLACE_ENTRY_ICON_SIZE_MOBILE,
} from '../../../constants/marketplace';
import { HOVER_MEDIA, LOCKED_OPACITY } from '../../../constants/ui';
import { isActivationKey } from '../../../util/ui';

interface IProps {
  onBrowse: () => void;
  isDisabled?: boolean;
}

const textColumnSx: SxProps<Theme> = { flex: 1, minWidth: 0 };

export const MarketplaceEntryBanner: React.FC<IProps> = ({ onBrowse, isDisabled = false }) => {
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));

  const handleBrowse = useCallback(() => {
    if (!isDisabled) {
      onBrowse();
    }
  }, [onBrowse, isDisabled]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (isActivationKey(event)) {
        event.preventDefault();
        handleBrowse();
      }
    },
    [handleBrowse],
  );

  const handleButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      handleBrowse();
    },
    [handleBrowse],
  );

  const containerSx: SxProps<Theme> = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      gap: isMobileViewport ? 1.5 : 2,
      p: 2,
      mb: 3,
      borderRadius: 1,
      cursor: isDisabled ? 'default' : 'pointer',
      ...(isDisabled && { opacity: LOCKED_OPACITY, pointerEvents: 'none' }),
      border: ({ palette }) => `1px solid ${alpha(palette.primary.main, 0.3)}`,
      backgroundColor: ({ palette }) => alpha(palette.primary.main, palette.action.hoverOpacity),
      transition: ({ transitions }) =>
        transitions.create(['background-color'], { duration: transitions.duration.shorter }),
      [HOVER_MEDIA]: {
        '&:hover': {
          backgroundColor: ({ palette }) => alpha(palette.primary.main, palette.action.focusOpacity),
        },
      },
    }),
    [isMobileViewport, isDisabled],
  );

  const iconWrapperSx: SxProps<Theme> = useMemo(() => {
    const size = isMobileViewport ? MARKETPLACE_ENTRY_ICON_SIZE_MOBILE : MARKETPLACE_ENTRY_ICON_SIZE;

    return {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: ({ palette }) => alpha(palette.primary.main, palette.action.selectedOpacity),
    };
  }, [isMobileViewport]);

  return (
    <Box
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      aria-label={BROWSE_MARKETPLACE_LABEL}
      aria-disabled={isDisabled}
      onClick={handleBrowse}
      onKeyDown={handleKeyDown}
      sx={containerSx}
    >
      <Box sx={iconWrapperSx}>
        <StorefrontOutlinedIcon color="primary" sx={{ fontSize: isMobileViewport ? 22 : 28 }} />
      </Box>
      <Box sx={textColumnSx}>
        <Typography variant={isMobileViewport ? 'body2' : 'body1'} fontWeight="medium">
          {ENTRY_BANNER_TITLE}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {ENTRY_BANNER_BODY}
        </Typography>
      </Box>
      {isMobileViewport ? (
        // The whole card is the tap target on mobile; a second labelled button would break the row.
        <ChevronRightIcon color="primary" sx={{ flexShrink: 0, fontSize: 24 }} />
      ) : (
        <Button
          variant="text"
          color="primary"
          size="medium"
          endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
          disabled={isDisabled}
          onClick={handleButtonClick}
          sx={{ flexShrink: 0 }}
        >
          {BROWSE_MARKETPLACE_LABEL}
        </Button>
      )}
    </Box>
  );
};
